import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Card from '../../ui/Card.jsx';
import ChartLegend from './Legend.jsx';
import { getActiveTheme, resolveThemeName } from '../../ui/tokens';
import { formatMinutesToHM } from '../../ui/format';

function MinutesTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="text-xs rounded border bg-white dark:bg-gray-900 p-2">
      <div className="font-medium mb-1">{label}</div>
      {payload.map((p,i)=>(
        <div key={i} className="flex justify-between gap-4">
          <span className="text-gray-600 dark:text-gray-300">{p.name}</span>
          <span>{formatMinutesToHM(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function WorkloadByPersonChart({ people = [], selectedUserId = null, onPersonClick, loading = false }) {
  const theme = getActiveTheme();
  const isPremiumLight = resolveThemeName() === 'auroraExecutiveLight' || resolveThemeName() === 'redLightPremium';
  const gridOpacity = Number(theme?.chartGridOpacity ?? (isPremiumLight ? 0.06 : 0.12));
  const gridStroke = isPremiumLight ? `rgba(15, 23, 42, ${gridOpacity})` : (theme?.border || '#e5e7eb');
  const axisStroke = isPremiumLight ? 'rgba(15, 23, 42, 0.14)' : (theme?.border || '#e5e7eb');
  const rows = useMemo(() => {
    const all = (people || []).map(u => ({
      name: u.display_name || '—',
      assigned: Number(u.logged_assigned_minutes || 0),
      other: Number(u.logged_other_minutes || 0),
      total: Number(u.logged_total_minutes || 0),
      user_id: u.user_id,
    }));
    const filtered = selectedUserId ? all.filter(r => r.user_id === selectedUserId) : all;
    return filtered.sort((a,b)=> b.total - a.total).slice(0, 12);
  }, [people, selectedUserId]);

  return (
    <Card padded={true} className="w-full">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold">Workload by Person</div>
        <ChartLegend items={[{ label: 'Assigned', color: theme.primary }, { label: 'Other', color: theme.warning }]} />
      </div>
      <div className="h-[260px]">
        {loading && (<div className="absolute inset-0 bg-black/5 dark:bg-white/5 flex items-center justify-center text-xs">Loading…</div>)}
        {(!rows || rows.length===0) ? (
          <div className="h-full flex items-center justify-center text-xs text-gray-600 dark:text-gray-400">No data for selected range</div>
        ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} onClick={(e)=>{
            const idx = e && e.activeTooltipIndex;
            if (typeof idx === 'number') {
              const r = rows[idx];
              if (r && onPersonClick) onPersonClick(r.user_id);
            }
          }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              dataKey="name"
              interval={0}
              tick={isPremiumLight ? { fill: theme?.mutedText || '#6B7280', fontSize: 12 } : undefined}
              axisLine={isPremiumLight ? { stroke: axisStroke } : undefined}
              tickLine={isPremiumLight ? { stroke: axisStroke } : undefined}
            />
            <YAxis
              tickFormatter={(v) => {
                const mins = Number(v || 0);
                const h = mins / 60;
                if (!Number.isFinite(h)) return '';
                const rounded = h >= 10 ? Math.round(h) : Math.round(h * 10) / 10;
                return `${rounded}h`;
              }}
              tick={isPremiumLight ? { fill: theme?.mutedText || '#6B7280', fontSize: 12 } : undefined}
              axisLine={isPremiumLight ? { stroke: axisStroke } : undefined}
              tickLine={isPremiumLight ? { stroke: axisStroke } : undefined}
            />
            <Tooltip content={<MinutesTooltip />} />
            <Bar dataKey="assigned" name="Assigned" stackId="a" fill={theme.primary} />
            <Bar dataKey="other" name="Other" stackId="a" fill={theme.warning} />
          </BarChart>
        </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}
